import React, { Component } from "react";
import {
    message,
    PageHeader,
    Spin,
    Row,
    Col,
    Tag,
    Card,
    Form as FormInitial,
    Typography,
    Tooltip,
    Upload,
    Input,
    InputNumber,
} from "antd";
import { DownloadOutlined, UploadOutlined, BarcodeOutlined } from "@ant-design/icons";
import {
    Form,
    FormItem,
    InputNumberItem,
    ButtonItem,
    ErrorItem,
    BarcodeScanner,
    round6Decimals,
} from "../../../../Components/Items";
import axios from "axios";
import { connect } from "react-redux";
import * as XLSX from "xlsx";
import { encodeId, decodeId } from "../../../../Components/idEncoder";

const { Text } = Typography;
const MODO_LABEL = { 1: "Ciego", 2: "Semi-ciego", 3: "Abierto" };

const TH_BASE = { background: "#e6f7fa", color: "#006d75", fontWeight: 600, padding: "10px 12px", whiteSpace: "nowrap" };
const TH_LEFT = TH_BASE;
const TH_NUM = (extra = {}) => ({ ...TH_BASE, textAlign: "right", ...extra });
const TD_BASE = { padding: "8px 12px", verticalAlign: "middle" };
const TD_LEFT = TD_BASE;
const TD_RIGHT = (extra = {}) => ({ ...TD_BASE, textAlign: "right", ...extra });
const TD_CENTER = (extra = {}) => ({ ...TD_BASE, textAlign: "center", ...extra });

// Texto corto de la celda: solo las claves de las ubicaciones donde hay existencia. A proposito no lleva
// cantidades, porque revelarlas filtraria la existencia del sistema y romperia el conteo Ciego.
const textoUbicaciones = (entrada) => {
    if (!entrada) return "—";
    const claves = (entrada.ubicaciones || []).map((u) => u.clave).filter(Boolean);
    if (entrada.sinUbicacion) claves.push("Sin ubicación");
    return claves.length > 0 ? claves.join(", ") : "—";
};

// Detalle largo (clave - nombre) para el tooltip, sin ensanchar la tabla.
const detalleUbicaciones = (entrada) => {
    if (!entrada) return null;
    const lineas = (entrada.ubicaciones || []).map((u) =>
        u.nombre ? `${u.clave} - ${u.nombre}` : u.clave
    ).filter(Boolean);
    if (entrada.sinUbicacion) lineas.push("Sin ubicación asignada");
    if (lineas.length === 0) return null;
    return (
        <div>
            {lineas.map((linea, indice) => (
                <div key={indice}>{linea}</div>
            ))}
        </div>
    );
};

class ContarOrdenDeConteo extends Component {
    formRef = React.createRef();
    codigoRef = React.createRef();
    cantidadRef = React.createRef();
    rowRefs = {};          // { indice de renglon: elemento <tr> }, para hacer scroll al escanear
    timerResaltado = null;

    state = {
        loading: false,
        submitting: false,
        ordenDeConteo: null,
        ordenEstado: null,
        insumosMap: {},
        ubicacionesPorInsumo: {}, // { insumoId: { ubicaciones: [{ubicacionId, clave, nombre}], sinUbicacion } }
        conteoInsumos: [], // [{ insumoId, locked, estado, cantidadContada, cantidadProyecto }]
        rondaActual: 1,
        id: this.props.match.params.id,
        codigoEscaneo: "",         // ultimo codigo leido o escrito en la barra de escaneo
        cantidadEscaneo: null,     // cantidad que se va a SUMAR al renglon resuelto
        insumoEscaneadoIdx: null,  // indice del renglon resuelto por el ultimo escaneo, o null
        resaltadoIdx: null,        // indice del renglon resaltado momentaneamente
    };

    componentDidMount() {
        this.consultarTodo();
        // Escaner fisico (wedge de teclado). Se arma con varias teclas muy seguidas, asi que
        // teclear cantidades a mano en la tabla no lo dispara.
        this.barcodeScanner = new BarcodeScanner({
            onScan: this.onScanCodigo.bind(this),
            getLoadingState: () => this.state.loading || this.state.submitting,
        });
    }

    componentWillUnmount() {
        this.barcodeScanner?.destroy();
        clearTimeout(this.timerResaltado);
    }

    // Calcula la ronda actual y el estado de lock de cada insumo
    computeConteoInsumos(insumos, proyectoValues = {}) {
        // Ronda actual = min de conteos entre los insumos no finalizados + 1
        const noFinalizados = insumos.filter((i) => i.estado !== 3);
        const minConteos =
            noFinalizados.length > 0
                ? Math.min(...noFinalizados.map((i) => i.conteos?.length ?? 0))
                : 0;
        const rondaActual = minConteos + 1;

        const conteoInsumos = insumos.map((item) => {
            const conteos = item.conteos ?? [];
            const locked = item.estado === 3 || conteos.length >= rondaActual;
            const ultimoConteo = conteos[conteos.length - 1];
            return {
                insumoId: item.insumoId,
                locked,
                estado: item.estado ?? 1,
                cantidadContada: ultimoConteo?.cantidadContada ?? item.cantidadContada ?? null,
                cantidadProyecto: proyectoValues[item.insumoId] ?? item.cantidadProyecto ?? null,
                conteos,
            };
        });

        return { rondaActual, conteoInsumos };
    }

    // Aplica conteoInsumos al estado y al form
    applyConteoInsumos(conteoInsumos, rondaActual, extraState = {}) {
        const formValues = conteoInsumos.map((item) => ({
            // Para bloqueados: mostrar el valor del conteo de esta ronda
            cantidadContada: item.locked
                ? (item.conteos[rondaActual - 1]?.cantidadContada ?? item.cantidadContada ?? null)
                : null,
            cantidadProyecto: item.cantidadProyecto,
        }));

        this.setState(
            { conteoInsumos, rondaActual, ...extraState },
            () => this.formRef.current?.setFieldsValue({ insumos: formValues })
        );
    }

    async consultarTodo() {
        this.setState({ loading: true });
        try {
            const res = await axios.post(
                "ordenesDeConteo/datosContarOrdenDeConteo",
                {
                    empresaId: this.props.empresaId,
                    ordenDeConteoId: this.state.id,
                },
                { headers: this.props.headersToken }
            );

            if (res.data.success === 2) {
                const { ordenDeConteo, insumos, ubicacionesPorInsumo } = res.data;

                const insumosMap = {};
                (insumos || []).forEach((i) => { insumosMap[i._id] = i; });

                let proyectoValues = {};
                if (ordenDeConteo.modoConteo === 3 || ordenDeConteo.modoConteo === 2) {
                    proyectoValues = await this.fetchInventario(
                        ordenDeConteo.insumos,
                        ordenDeConteo.proyectoId
                    );
                }

                const { rondaActual, conteoInsumos } = this.computeConteoInsumos(
                    ordenDeConteo.insumos,
                    proyectoValues
                );

                this.applyConteoInsumos(conteoInsumos, rondaActual, {
                    ordenDeConteo,
                    insumosMap,
                    ubicacionesPorInsumo: ubicacionesPorInsumo || {},
                    ordenEstado: ordenDeConteo.estado,
                });
            } else {
                message.error("Error al cargar la orden de conteo");
            }
        } catch {
            message.error("Error de conexión");
        }
        this.setState({ loading: false });
    }

    // Devuelve un mapa { insumoId: cantidadProyecto }
    async fetchInventario(ordenInsumos, proyectoId) {
        const proyectoValues = {};
        try {
            const payload = ordenInsumos.map((item, index) => ({
                index,
                insumoId: item.insumoId,
                proyectoId,
                inventariable: true
            }));
            const res = await axios.post(
                "inventarios/inventarioInsumoProyectoBulk",
                { empresaId: this.props.empresaId, proyectoId, insumos: payload },
                { headers: this.props.headersToken }
            );
            if (res.data?.results) {
                res.data.results.forEach((r) => {
                    proyectoValues[ordenInsumos[r.index]?.insumoId] = r.cantidad ?? 0;
                });
            }
        } catch {
            message.warning("No se pudo obtener el inventario actual");
        }
        return proyectoValues;
    }

    onFinishFailed(e) {
        ErrorItem(e);
    }

    async onFinish(values) {
        const { conteoInsumos } = this.state;

        const insumos = (values.insumos || [])
            .map((v, idx) => ({
                insumoId: conteoInsumos[idx]?.insumoId,
                locked: conteoInsumos[idx]?.locked,
                cantidadContada: v.cantidadContada,
            }))
            .filter(
                (i) =>
                    !i.locked &&
                    i.insumoId &&
                    i.cantidadContada !== null &&
                    i.cantidadContada !== undefined
            );

        if (!insumos.length) {
            message.warning("Ingresa al menos una cantidad");
            return;
        }

        this.setState({ submitting: true });
        try {
            const res = await axios.post(
                "ordenesDeConteo/conteoOrdenDeConteo",
                {
                    empresaId: this.props.empresaId,
                    ordenDeConteoId: this.state.id,
                    insumos: insumos.map((i) => ({
                        insumoId: i.insumoId,
                        cantidadContada: i.cantidadContada,
                    })),
                },
                { headers: this.props.headersToken }
            );

            if (res.data.success === 2) {
                message.success("Conteo registrado");

                // Preservar cantidadProyecto que ya teníamos
                const prevProyectoValues = {};
                this.state.conteoInsumos.forEach((ci) => {
                    prevProyectoValues[ci.insumoId] = ci.cantidadProyecto;
                });

                const { rondaActual, conteoInsumos } = this.computeConteoInsumos(
                    res.data.insumos || [],
                    prevProyectoValues
                );

                this.applyConteoInsumos(conteoInsumos, rondaActual, {
                    ordenEstado: res.data.estado,
                });
            } else {
                message.error("No se pudo registrar el conteo");
            }
        } catch {
            message.error("Error de conexión");
        }
        this.setState({ submitting: false });
    }

    atras() {
        this.props.history.goBack();
    }

    // La columna solo aplica si la empresa usa WMS y el almacen realmente tiene ubicaciones con existencia.
    mostrarUbicacion() {
        return !!this.props.moduloWms && Object.keys(this.state.ubicacionesPorInsumo).length > 0;
    }

    // La columna aparece solo si algun insumo de la orden trae codigo de barras capturado, para no
    // ensanchar la tabla con una columna de puros guiones. Mismo criterio que mostrarUbicacion.
    mostrarCodigoBarras() {
        const { conteoInsumos, insumosMap } = this.state;
        return conteoInsumos.some((ci) => !!insumosMap[ci.insumoId]?.codigoBarras);
    }

    // Devuelve TODOS los indices de renglon cuyo insumo coincide con el codigo. Se busca solo dentro
    // de la orden: si el codigo existe en el catalogo pero no esta en esta orden, no hay nada que
    // contar. Ademas se resuelve 100% en el cliente, asi no se filtra existencia en modo Ciego.
    buscarIndicesPorCodigo(codigo) {
        const objetivo = String(codigo || "").trim().toLowerCase();
        if (!objetivo) return [];
        const { conteoInsumos, insumosMap } = this.state;
        const indices = [];
        conteoInsumos.forEach((ci, idx) => {
            const insumo = insumosMap[ci.insumoId];
            if (!insumo) return;
            const candidatos = [insumo.codigoBarras, insumo.clave, insumo.codigo];
            if (candidatos.some((c) => c && String(c).trim().toLowerCase() === objetivo)) {
                indices.push(idx);
            }
        });
        return indices;
    }

    resaltarRenglon(indice) {
        clearTimeout(this.timerResaltado);
        this.setState({ resaltadoIdx: indice }, () => {
            this.rowRefs[indice]?.scrollIntoView({ behavior: "smooth", block: "center" });
        });
        this.timerResaltado = setTimeout(() => this.setState({ resaltadoIdx: null }), 2500);
    }

    limpiarEscaneo() {
        this.setState(
            { codigoEscaneo: "", cantidadEscaneo: null, insumoEscaneadoIdx: null },
            () => this.codigoRef.current?.focus()
        );
    }

    nombreInsumoDeRenglon(indice) {
        const ci = this.state.conteoInsumos[indice];
        return this.state.insumosMap[ci?.insumoId]?.stringBuscar || "Insumo";
    }

    onScanCodigo(codigo) {
        const limpio = String(codigo || "").trim();
        if (!limpio) return;
        if (this.state.ordenEstado === 3) return; // orden finalizada: no hay nada que capturar

        const coincidencias = this.buscarIndicesPorCodigo(limpio);

        if (coincidencias.length === 0) {
            this.setState({ codigoEscaneo: limpio, cantidadEscaneo: null, insumoEscaneadoIdx: null });
            message.error(`El código ${limpio} no corresponde a ningún insumo de esta orden`);
            return;
        }
        if (coincidencias.length > 1) {
            message.warning(
                `Hay ${coincidencias.length} insumos de esta orden con el código ${limpio}; se tomó el primero`
            );
        }

        const indice = coincidencias[0];
        this.resaltarRenglon(indice);

        if (this.state.conteoInsumos[indice]?.locked) {
            this.setState({ codigoEscaneo: limpio, cantidadEscaneo: null, insumoEscaneadoIdx: null });
            message.warning(`${this.nombreInsumoDeRenglon(indice)} ya se contó en esta ronda`);
            return;
        }

        this.setState(
            { codigoEscaneo: limpio, cantidadEscaneo: null, insumoEscaneadoIdx: indice },
            () => this.cantidadRef.current?.focus()
        );
    }

    // Suma la cantidad tecleada a lo que ya lleva el renglon resuelto, para poder contar el mismo
    // insumo en varias pasadas (tarimas, cajas) sin borrar lo anterior.
    aplicarCantidadEscaneo() {
        const { insumoEscaneadoIdx, cantidadEscaneo, conteoInsumos } = this.state;

        if (insumoEscaneadoIdx == null) {
            message.warning("Primero escanea o escribe un código");
            return;
        }
        if (cantidadEscaneo === null || cantidadEscaneo === undefined || cantidadEscaneo === "") {
            message.warning("Escribe la cantidad a sumar");
            return;
        }
        const cantidad = Number(cantidadEscaneo);
        if (!Number.isFinite(cantidad)) {
            message.warning("La cantidad a sumar no es un número válido");
            return;
        }
        if (conteoInsumos[insumoEscaneadoIdx]?.locked) {
            message.warning(`${this.nombreInsumoDeRenglon(insumoEscaneadoIdx)} ya se contó en esta ronda`);
            this.limpiarEscaneo();
            return;
        }

        const valores = this.formRef.current?.getFieldValue("insumos") || [];
        const actualizados = [...valores];
        const previo = actualizados[insumoEscaneadoIdx]?.cantidadContada;
        const actual = previo === null || previo === undefined || previo === "" ? 0 : Number(previo);
        const base = Number.isFinite(actual) ? actual : 0;
        // Se redondea porque las cantidades pueden ser fraccionarias y sumar en punto flotante
        // deja colas del tipo 0.30000000000000004.
        const nuevo = round6Decimals(base + cantidad);

        actualizados[insumoEscaneadoIdx] = {
            ...(actualizados[insumoEscaneadoIdx] || {}),
            cantidadContada: nuevo,
        };
        this.formRef.current?.setFieldsValue({ insumos: actualizados });

        message.success(`${this.nombreInsumoDeRenglon(insumoEscaneadoIdx)}: ${base} + ${cantidad} = ${nuevo}`);
        this.limpiarEscaneo();
    }

    descargarExcel() {
        const { conteoInsumos, insumosMap, ubicacionesPorInsumo, ordenDeConteo } = this.state;
        const pendientes = conteoInsumos.filter((ci) => !ci.locked);
        if (!pendientes.length) {
            message.warning("No hay insumos pendientes de contar");
            return;
        }
        const mostrarUbicacion = this.mostrarUbicacion();
        const mostrarCodigoBarras = this.mostrarCodigoBarras();
        const rows = pendientes.map((ci) => ({
            ID: encodeId(String(ci.insumoId)),
            Insumo: insumosMap[ci.insumoId]?.stringBuscar || "",
            ...(mostrarCodigoBarras
                ? { "Código de Barras": insumosMap[ci.insumoId]?.codigoBarras || "" }
                : {}),
            ...(mostrarUbicacion
                ? { "Ubicación": textoUbicaciones(ubicacionesPorInsumo[String(ci.insumoId)]) }
                : {}),
            "Cantidad Contada": "",
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        // Los anchos van en el mismo orden que las llaves de arriba, asi que las columnas
        // opcionales se insertan condicionalmente para no descuadrarlos.
        ws["!cols"] = [
            { wch: 16 },                                    // ID
            { wch: 40 },                                    // Insumo
            ...(mostrarCodigoBarras ? [{ wch: 22 }] : []),  // Código de Barras
            ...(mostrarUbicacion ? [{ wch: 26 }] : []),     // Ubicación
            { wch: 18 },                                    // Cantidad Contada
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Conteo");
        const nombre = ordenDeConteo?.nombre || "conteo";
        XLSX.writeFile(wb, `${nombre}.xlsx`);
    }

    cargarExcel(file) {
        const { conteoInsumos } = this.state;
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const wb = XLSX.read(e.target.result, { type: "array" });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const rows = XLSX.utils.sheet_to_json(ws);

                // Mapa insumoId -> índice en conteoInsumos
                const idxMap = {};
                conteoInsumos.forEach((ci, idx) => {
                    idxMap[String(ci.insumoId)] = idx;
                });

                const formValues = this.formRef.current?.getFieldValue("insumos") || [];
                const updated = [...formValues];
                let aplicados = 0;

                rows.forEach((row) => {
                    const encodedId = row["ID"];
                    const cantidad = row["Cantidad Contada"];
                    if (!encodedId || cantidad === "" || cantidad === undefined || cantidad === null) return;
                    const decodedId = decodeId(String(encodedId));
                    if (!decodedId) return;
                    const idx = idxMap[decodedId];
                    if (idx === undefined) return;
                    if (conteoInsumos[idx]?.locked) return;
                    updated[idx] = { ...(updated[idx] || {}), cantidadContada: Number(cantidad) };
                    aplicados++;
                });

                this.formRef.current?.setFieldsValue({ insumos: updated });
                message.success(`${aplicados} cantidad${aplicados !== 1 ? "es" : ""} cargada${aplicados !== 1 ? "s" : ""}`);
            } catch {
                message.error("Error al leer el archivo Excel");
            }
        };
        reader.readAsArrayBuffer(file);
        return false; // evita upload automático de Ant Design
    }

    render() {
        const {
            loading,
            submitting,
            ordenDeConteo,
            ordenEstado,
            insumosMap,
            ubicacionesPorInsumo,
            conteoInsumos,
            rondaActual,
            codigoEscaneo,
            cantidadEscaneo,
            insumoEscaneadoIdx,
            resaltadoIdx,
        } = this.state;

        const mostrarUbicacion = this.mostrarUbicacion();
        const mostrarCodigoBarras = this.mostrarCodigoBarras();
        const modo = ordenDeConteo?.modoConteo;
        const finalizada = ordenEstado === 3;
        const mostrarInventario = modo === 3 || (modo === 2 && rondaActual > 1);
        const maxConteos = conteoInsumos.length > 0
            ? Math.max(...conteoInsumos.map((ci) => ci.conteos?.length ?? 0))
            : 0;
        const nHistorico = finalizada ? maxConteos : rondaActual - 1;
        const mostrarColumnaActiva = !finalizada;
        const hayAbiertos = conteoInsumos.some((ci) => !ci.locked);
        const vencida = !!ordenDeConteo?.fechaVigencia
            && !finalizada
            && new Date(ordenDeConteo.fechaVigencia).setHours(23, 59, 59, 999) < Date.now();

        return (
            <>
                <PageHeader
                    onBack={this.atras.bind(this)}
                    title="Contar orden de conteo"
                    style={{
                        border: "1px solid rgb(235, 237, 240)",
                        backgroundColor: "white",
                        marginBottom: 10,
                    }}
                />
                <div className="card-background">
                    <Spin spinning={loading}>
                        {ordenDeConteo && (
                            <Card
                                style={{ marginBottom: 16, borderRadius: 8, border: "1px solid #d9f2f5", background: "#f0f9fa" }}
                                bodyStyle={{ padding: "12px 18px" }}
                            >
                                <div style={{ marginBottom: 10 }}>
                                    <Text strong style={{ fontSize: 15, marginRight: 10 }}>
                                        {ordenDeConteo.nombre}
                                    </Text>
                                    <Tag color="cyan">{MODO_LABEL[modo] || "—"}</Tag>
                                    {finalizada && <Tag color="green">Finalizada</Tag>}
                                    {ordenEstado === 2 && <Tag color="orange">En conteo</Tag>}
                                    {ordenEstado === 1 && <Tag color="blue">Pendiente</Tag>}
                                    {vencida && <Tag color="red">Vencida</Tag>}
                                </div>
                                {vencida && (
                                    <div style={{ marginBottom: 10, color: "#f5222d", fontSize: 12 }}>
                                        Esta orden venció el {new Date(ordenDeConteo.fechaVigencia).toLocaleDateString("es-MX")}. Aún puedes registrar el conteo, pero ya está fuera de su fecha de vigencia.
                                    </div>
                                )}
                                <Row gutter={[24, 4]}>
                                    {ordenDeConteo.responsableNombre && (
                                        <Col>
                                            <Text type="secondary" style={{ fontSize: 11 }}>Responsable</Text>
                                            <br />
                                            <Text style={{ fontSize: 13 }}>{ordenDeConteo.responsableNombre}</Text>
                                        </Col>
                                    )}
                                    {ordenDeConteo.proyectoNombre && (
                                        <Col>
                                            <Text type="secondary" style={{ fontSize: 11 }}>Proyecto</Text>
                                            <br />
                                            <Text style={{ fontSize: 13 }}>{ordenDeConteo.proyectoNombre}</Text>
                                        </Col>
                                    )}
                                    <Col>
                                        <Text type="secondary" style={{ fontSize: 11 }}>Ronda actual</Text>
                                        <br />
                                        <Text style={{ fontSize: 13 }}>
                                            {rondaActual === 1 ? "Conteo 1" : `Reconteo ${rondaActual - 1}`}
                                        </Text>
                                    </Col>
                                    {ordenDeConteo.fechaVigencia && (
                                        <Col>
                                            <Text type="secondary" style={{ fontSize: 11 }}>Vigencia</Text>
                                            <br />
                                            <Text style={{ fontSize: 13, color: vencida ? "#f5222d" : undefined }}>
                                                {new Date(ordenDeConteo.fechaVigencia).toLocaleDateString("es-MX")}
                                            </Text>
                                        </Col>
                                    )}
                                </Row>
                            </Card>
                        )}

                        {!finalizada && hayAbiertos && (
                            <Card
                                style={{ marginBottom: 16, borderRadius: 8, border: "1px solid #87e8de", background: "#e6fffb" }}
                                bodyStyle={{ padding: "12px 18px" }}
                            >
                                <Row gutter={[16, 8]} align="bottom">
                                    <Col>
                                        <Text type="secondary" style={{ fontSize: 11 }}>Código</Text>
                                        <br />
                                        <Input
                                            ref={this.codigoRef}
                                            value={codigoEscaneo}
                                            allowClear
                                            prefix={<BarcodeOutlined style={{ color: "#8c8c8c" }} />}
                                            placeholder="Escanea o escribe el código y presiona Enter"
                                            style={{ width: 330 }}
                                            onChange={(e) => this.setState({ codigoEscaneo: e.target.value })}
                                            onPressEnter={() => this.onScanCodigo(this.state.codigoEscaneo)}
                                        />
                                    </Col>
                                    <Col>
                                        <Text type="secondary" style={{ fontSize: 11 }}>Cantidad a sumar</Text>
                                        <br />
                                        <InputNumber
                                            ref={this.cantidadRef}
                                            value={cantidadEscaneo}
                                            placeholder="Cantidad"
                                            style={{ width: 150 }}
                                            disabled={insumoEscaneadoIdx == null}
                                            onChange={(valor) => this.setState({ cantidadEscaneo: valor })}
                                            onPressEnter={this.aplicarCantidadEscaneo.bind(this)}
                                        />
                                    </Col>
                                    <Col>
                                        <ButtonItem
                                            type="primary"
                                            disabled={insumoEscaneadoIdx == null}
                                            onClick={this.aplicarCantidadEscaneo.bind(this)}
                                        >
                                            Sumar
                                        </ButtonItem>
                                    </Col>
                                    <Col flex="auto">
                                        <Text style={{ fontSize: 12, color: insumoEscaneadoIdx != null ? "#006d75" : "#8c8c8c" }}>
                                            {insumoEscaneadoIdx != null
                                                ? `→ ${this.nombreInsumoDeRenglon(insumoEscaneadoIdx)} (renglón ${insumoEscaneadoIdx + 1})`
                                                : "Escanea un código para ubicar el insumo en la tabla y sumarle la cantidad contada."}
                                        </Text>
                                    </Col>
                                </Row>
                            </Card>
                        )}

                        <Form
                            ref={this.formRef}
                            name="contarOrdenDeConteo"
                            layout
                            bottom={null}
                            onFinish={this.onFinish.bind(this)}
                            onFinishFailed={this.onFinishFailed}
                        >
                            <div style={{ overflowX: "auto" }}>
                                <FormInitial.List name="insumos">
                                    {(fields) => (
                                        <table className="table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                                            {fields.length > 0 && (
                                                <thead>
                                                    <tr>
                                                        <th className="th-border" style={TH_NUM({ width: 36 })}>#</th>
                                                        <th className="th-border" style={TH_LEFT}>Insumo</th>
                                                        {mostrarCodigoBarras && (
                                                            <th className="th-border" style={TH_LEFT}>Cód. barras</th>
                                                        )}
                                                        {mostrarUbicacion && (
                                                            <th className="th-border" style={TH_LEFT}>Ubicación</th>
                                                        )}
                                                        {mostrarInventario && (
                                                            <th className="th-border" style={TH_NUM({ width: "13%" })}>
                                                                Inv. actual
                                                            </th>
                                                        )}
                                                        {Array.from({ length: nHistorico }, (_, i) => (
                                                            <th key={`h${i}`} className="th-border" style={TH_NUM({ width: "12%" })}>
                                                                {i === 0 ? "Conteo 1" : `Reconteo ${i}`}
                                                            </th>
                                                        ))}
                                                        {mostrarColumnaActiva && (
                                                            <th className="th-border" style={TH_NUM({ width: "15%" })}>
                                                                {rondaActual === 1 ? "Conteo 1" : `Reconteo ${rondaActual - 1}`}
                                                            </th>
                                                        )}
                                                    </tr>
                                                </thead>
                                            )}
                                            <tbody>
                                                {fields.map(({ key, name, fieldKey, ...restField }) => {
                                                    const ci = conteoInsumos[name];
                                                    const insumo = insumosMap[ci?.insumoId];
                                                    const ultimoConteo = ci?.conteos?.length > 0 ? ci.conteos[ci.conteos.length - 1] : null;
                                                    const coincide = ultimoConteo != null && ultimoConteo.cantidadContada === ultimoConteo.snapshot;

                                                    const rowBg = ultimoConteo
                                                        ? ci?.locked
                                                            ? coincide ? "#f6ffed" : "#fff1f0"
                                                            : "#fffbe6"
                                                        : undefined;

                                                    // El resaltado del escaneo gana sobre el color semantico, pero dura
                                                    // solo un momento y despues el renglon vuelve a su color normal.
                                                    const resaltado = resaltadoIdx === name;
                                                    const trStyle = resaltado
                                                        ? { background: "#b5f5ec" }
                                                        : (rowBg ? { background: rowBg } : undefined);

                                                    return (
                                                        <tr
                                                            key={key}
                                                            ref={(el) => { this.rowRefs[name] = el; }}
                                                            style={trStyle}
                                                        >
                                                            <td
                                                                className="td-border"
                                                                style={TD_CENTER({
                                                                    color: "#aaa",
                                                                    borderLeft: resaltado ? "3px solid #00BFD6" : undefined,
                                                                })}
                                                            >
                                                                {name + 1}
                                                            </td>
                                                            <td className="td-border" style={TD_LEFT}>
                                                                {ci?.locked && ultimoConteo && (
                                                                    <Tag color={coincide ? "green" : "red"} style={{ marginRight: 6 }}>
                                                                        {coincide ? "✓" : "✗"}
                                                                    </Tag>
                                                                )}
                                                                {insumo?.stringBuscar || "—"}
                                                            </td>
                                                            {mostrarCodigoBarras && (
                                                                <td className="td-border" style={TD_LEFT}>
                                                                    <span style={{ color: "#595959", fontFamily: "monospace" }}>
                                                                        {insumo?.codigoBarras || "—"}
                                                                    </span>
                                                                </td>
                                                            )}
                                                            {mostrarUbicacion && (() => {
                                                                const entradaUbicacion = ubicacionesPorInsumo[String(ci?.insumoId)];
                                                                const detalle = detalleUbicaciones(entradaUbicacion);
                                                                const texto = (
                                                                    <span style={{ color: "#595959" }}>
                                                                        {textoUbicaciones(entradaUbicacion)}
                                                                    </span>
                                                                );
                                                                return (
                                                                    <td className="td-border" style={TD_LEFT}>
                                                                        {detalle ? <Tooltip title={detalle}>{texto}</Tooltip> : texto}
                                                                    </td>
                                                                );
                                                            })()}
                                                            {mostrarInventario && (
                                                                <td className="td-border" style={TD_RIGHT()}>
                                                                    <FormItem
                                                                        {...restField}
                                                                        name={[name, "cantidadProyecto"]}
                                                                        fieldKey={[fieldKey, "cantidadProyecto"]}
                                                                        noStyle
                                                                    >
                                                                        <InputNumberItem sinBorde disabled placeholder="—" />
                                                                    </FormItem>
                                                                </td>
                                                            )}
                                                            {Array.from({ length: nHistorico }, (_, i) => (
                                                                <td key={`h${i}`} className="td-border" style={TD_RIGHT({ color: "#595959" })}>
                                                                    {ci?.conteos?.[i]?.cantidadContada ?? "—"}
                                                                </td>
                                                            ))}
                                                            {mostrarColumnaActiva && (
                                                                <td className="td-border" style={TD_RIGHT()}>
                                                                    <FormItem
                                                                        {...restField}
                                                                        name={[name, "cantidadContada"]}
                                                                        fieldKey={[fieldKey, "cantidadContada"]}
                                                                        numberCero
                                                                        noStyle
                                                                    >
                                                                        <InputNumberItem
                                                                            sinBorde
                                                                            disabled={ci?.locked}
                                                                            placeholder={ci?.locked ? "—" : "Cantidad"}
                                                                            onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
                                                                        />
                                                                    </FormItem>
                                                                </td>
                                                            )}
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    )}
                                </FormInitial.List>
                            </div>

                            {!finalizada && hayAbiertos && (
                                <Row justify="space-between" align="middle" style={{ marginTop: 16 }}>
                                    <Col>
                                        <ButtonItem
                                            icon={<DownloadOutlined />}
                                            onClick={this.descargarExcel.bind(this)}
                                            style={{ marginRight: 8 }}
                                        >
                                            Descargar plantilla
                                        </ButtonItem>
                                        <Upload
                                            accept=".xlsx,.xls"
                                            showUploadList={false}
                                            beforeUpload={this.cargarExcel.bind(this)}
                                        >
                                            <ButtonItem icon={<UploadOutlined />}>
                                                Cargar Excel
                                            </ButtonItem>
                                        </Upload>
                                    </Col>
                                    <Col>
                                        <ButtonItem
                                            type="primary"
                                            htmlType="submit"
                                            loading={submitting}
                                        >
                                            Registrar conteo
                                        </ButtonItem>
                                    </Col>
                                </Row>
                            )}
                        </Form>
                    </Spin>
                </div>
            </>
        );
    }
}

const mapStateToProps = (state) => ({
    headersToken: state.user.headersToken,
    empresaId: state.empresa.id,
    moduloWms: state.empresa.moduloWms,
});

export default connect(mapStateToProps)(ContarOrdenDeConteo);
